import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { ArrowLeft, Home, Phone, AlertTriangle, CheckCircle, RotateCcw, User, MapPin, Loader2, ClipboardList, Share2, UserPlus, Building2, Search, ChevronRight, Shield, ArrowRight, Mail, Brain, Heart, Eye, MessageSquare, Download } from "lucide-react";
import { useGeolocation } from "../../../hooks/useGeolocation";
import type { AssessmentState, AssessmentStep, OutcomeType } from "../types/assessment.types";
import type { IncidentRecord } from "../../incident-logging/types/incident.types";
import type { OrgLocation } from "../../venue-management/types/venue.types";
import { RED_FLAGS, OBSERVABLE_SIGNS, SELF_SYMPTOMS, EMOTION_SYMPTOMS, THINKING_SYMPTOMS, AWARENESS_QUESTIONS, OUTCOMES } from "../data/decisionTree";
import { useAnonymousSessionOptional } from "../../../contexts/AnonymousSessionContext";
import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import { createIncident } from "../../incident-logging/services/incidentService";
import { getPublicVenue, getDefaultOrgVenue, getOrgLocations, findNearestOrgLocation, shareAssessmentWithClub } from "../../venue-management/services/venueService";
import { IncidentConfirmation } from "../../incident-logging/components/IncidentConfirmation";
import { downloadAssessmentAsPdf } from "../../incident-logging/utils/assessmentPdfExport";
import { theme } from "../../../theme";

// ─── Constants ────────────────────────────────────────────────────────────────

const ASSESSMENT_SESSION_KEY = "charlie_assessment_in_progress";
const ASSESSMENT_SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const MAX_INPUT_LENGTH = 500;

const STEPS: { key: AssessmentStep; label: string }[] = [
  { key: "sport-select", label: "Sport" },
  { key: "red-flags", label: "Red Flags" },
  { key: "observable-signs", label: "Visible Clues" },
  { key: "symptoms", label: "Symptoms" },
  { key: "awareness", label: "Awareness" },
];

// ─── Sport options ────────────────────────────────────────────────────────────

type SportOption = { id: string; label: string; icon: string; imageSrc?: string };

const SPORTS: SportOption[] = [
  { id: "rugby",         label: "Rugby",         icon: "🏉" },
  { id: "afl",           label: "AFL",           icon: "",   imageSrc: "/afl-ball.svg" },
  { id: "soccer",        label: "Soccer",        icon: "⚽" },
  { id: "basketball",    label: "Basketball",    icon: "🏀" },
  { id: "skateboarding", label: "Skateboarding", icon: "🛹" },
  { id: "mtb",           label: "MTB",           icon: "🚵" },
  { id: "bmx",           label: "BMX",           icon: "🚲" },
  { id: "cricket",       label: "Cricket",       icon: "🏏" },
  { id: "hockey",        label: "Hockey",        icon: "🏑" },
  { id: "netball",       label: "Netball",       icon: "🤾" },
  { id: "tennis",        label: "Tennis",        icon: "🎾" },
  { id: "judo",          label: "Judo",          icon: "🥋" },
  { id: "other",         label: "Other",         icon: "🏅" },
];

// ─── Shared components ────────────────────────────────────────────────────────

const BigButton = ({
  children,
  onClick,
  variant = "default",
  className = "",
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "danger" | "success" | "teal";
  className?: string;
  disabled?: boolean;
}) => {
  const variantClasses = {
    default: theme.button.bigDefault,
    danger:  theme.button.bigDanger,
    success: theme.button.bigSuccess,
    teal:    theme.button.bigTeal,
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${theme.button.bigBase} ${variantClasses[variant]} ${className} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

const EmergencyButton = () => (
  <a href="tel:000" className={`${theme.button.emergency} animate-emergency-pulse`}>
    <Phone className="w-5 h-5 mr-3 flex-shrink-0" />
    Call Triple Zero ( 000 )
  </a>
);

// ─── Progress indicator ───────────────────────────────────────────────────────

const ProgressIndicator = ({ currentStep }: { currentStep: AssessmentStep }) => {
  if (currentStep === "outcome") return null;
  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div
      className="mb-6"
      role="progressbar"
      aria-label={`Step ${stepIndex + 1} of 3: ${STEPS[stepIndex].label}`}
      aria-valuenow={stepIndex + 1}
      aria-valuemin={1}
      aria-valuemax={3}
    >
      {/* Step labels row */}
      <div className="flex items-center justify-between mb-2">
        {STEPS.map((step, i) => (
          <span
            key={step.key}
            className={`text-xs font-semibold transition-colors duration-200 ${
              i === stepIndex
                ? theme.text.accent
                : i < stepIndex
                ? theme.text.muted
                : theme.text.veryMuted
            }`}
          >
            {step.label}
          </span>
        ))}
      </div>

      {/* Progress bar track */}
      <div className="flex items-center gap-1.5">
        {STEPS.map((step, i) => (
          <div
            key={step.key}
            className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              i < stepIndex
                ? theme.step.connectorOn
                : i === stepIndex
                ? "dark:bg-sy-teal/40 bg-teal-200"
                : theme.step.connectorOff
            }`}
          />
        ))}
      </div>

      {/* Step count */}
      <p className={`text-xs ${theme.text.muted} mt-2 text-right`}>
        Step {stepIndex + 1} of {STEPS.length}
      </p>
    </div>
  );
};

// ─── Location capture card ─────────────────────────────────────────────────────

type LocationCaptureCardProps = {
  permissionState: 'prompt' | 'granted' | 'denied' | 'unavailable';
  isLoading: boolean;
  address: string | null;
  coordinates: { lat: number; lng: number } | null;
  manualAddress: string;
  skipped: boolean;
  onRequestLocation: () => void;
  onManualAddressChange: (v: string) => void;
  onSkip: () => void;
};

const LocationCaptureCard = ({
  permissionState,
  isLoading,
  address,
  coordinates,
  manualAddress,
  skipped,
  onRequestLocation,
  onManualAddressChange,
  onSkip,
}: LocationCaptureCardProps) => {
  if (skipped) return null;

  const captured = coordinates !== null;
  const showManualForm =
    (permissionState === 'denied' || permissionState === 'unavailable') && !captured;

  return (
    <div className={`text-left mt-4 mb-6 ${theme.card.paddedMuted}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={theme.icon.tealSm}>
          <MapPin className={`w-4 h-4 ${theme.text.accent}`} />
        </div>
        <span className={theme.text.accentLabel}>Location</span>
      </div>

      {!captured && !showManualForm && !isLoading && (
        <>
          <h3 className={`text-base font-bold ${theme.text.heading} mb-1`}>
            Capture your location?
          </h3>
          <p className={`${theme.text.body} text-sm mb-4`}>
            Stores the venue location with the incident report for your records.
          </p>
          <button
            onClick={onRequestLocation}
            className={`${theme.button.medBase} ${theme.button.medTeal} mb-2`}
          >
            Share My Location
          </button>
          <button onClick={onSkip} className={theme.button.ghost}>
            Skip
          </button>
        </>
      )}

      {isLoading && (
        <div className="flex items-center gap-3 py-2">
          <Loader2 className={`w-5 h-5 ${theme.text.accent} ${theme.anim.spin} flex-shrink-0`} />
          <span className={`${theme.text.body} text-sm`}>Getting your location…</span>
        </div>
      )}

      {captured && (
        <div className="flex items-start gap-3">
          <CheckCircle className={`w-5 h-5 ${theme.text.accent} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={`${theme.text.heading} font-medium text-sm`}>Location captured</p>
            {address && <p className={`${theme.text.body} text-sm`}>{address}</p>}
          </div>
        </div>
      )}

      {showManualForm && (
        <>
          <h3 className={`text-base font-bold ${theme.text.heading} mb-1`}>
            Enter your location
          </h3>
          <p className={`${theme.text.body} text-sm mb-4`}>
            Location access was denied. Enter the venue name or address manually.
          </p>
          <label htmlFor="manual-location" className="sr-only">
            Venue name or address
          </label>
          <input
            id="manual-location"
            type="text"
            value={manualAddress}
            onChange={(e) => onManualAddressChange(e.target.value.slice(0, 200))}
            placeholder="e.g. North Sydney Oval, Miller St, North Sydney"
            aria-label="Venue name or address"
            className={`${theme.input.base} mb-3`}
          />
          <button onClick={onSkip} className={theme.button.ghost}>
            Skip location
          </button>
        </>
      )}
    </div>
  );
};

// ─── Step screens ─────────────────────────────────────────────────────────────

const StartScreen = ({
  onStart,
  locationCaptureCard,
  hasProgress,
  onResume,
  onStartFresh,
  isLoggedIn,
}: {
  onStart: () => void;
  locationCaptureCard?: React.ReactNode;
  hasProgress?: boolean;
  onResume?: () => void;
  onStartFresh?: () => void;
  isLoggedIn?: boolean;
}) => (
  <div className={theme.anim.fadeInUp}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">

      {/* ── Left: info panel ─────────────────────── */}
      <div>
        <h1 className={`text-3xl font-bold ${theme.text.heading} mb-3 tracking-tight leading-tight`}>
          Sideline Concussion<br className="hidden sm:block" /> Assessment
        </h1>
        <p className={`${theme.text.body} mb-7 leading-relaxed text-sm max-w-sm`}>
          Concussion Recognition Tool 6. Helps identify potential concussion signs — <em>not</em> a medical diagnosis.
        </p>

        {/* Feature list */}
        <div className="space-y-3 mb-7">
          {[
            { icon: '⚡', text: 'Takes about 2–5 minutes' },
            { icon: '📋', text: 'Step-by-step guided questions' },
            { icon: '🔒', text: 'Results logged securely for your records' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <span className="text-base leading-none">{item.icon}</span>
              <span className={`text-sm ${theme.text.body}`}>{item.text}</span>
            </div>
          ))}
        </div>

        {/* Emergency callout */}
        <div className="flex items-start gap-2.5 rounded-xl dark:bg-sy-red/[0.08] bg-red-50 dark:border-sy-red/20 border border-red-200 px-3.5 py-2.5 mb-6">
          <AlertTriangle className="w-4 h-4 text-sy-red flex-shrink-0 mt-0.5" />
          <p className="text-xs dark:text-red-400 text-red-700 leading-relaxed">
            If the person is unconscious or you suspect a spinal injury,{' '}
            <strong>call 000 immediately.</strong>
          </p>
        </div>

        {/* View logged assessments */}
        {isLoggedIn && (
          <Link
            to="/assessments"
            className={`inline-flex items-center gap-2 text-sm font-medium dark:bg-white/[0.04] bg-gray-50 dark:border-white/[0.08] border border-gray-200 dark:text-white/60 text-gray-500 dark:hover:bg-white/[0.07] hover:bg-gray-100 dark:hover:text-white/85 hover:text-gray-700 rounded-xl px-4 py-2.5 ${theme.anim.transition}`}
          >
            <ClipboardList className="w-4 h-4" />
            View Logged Assessments
          </Link>
        )}
      </div>

      {/* ── Right: action panel ───────────────────── */}
      <div className="space-y-4">
        {locationCaptureCard}

        {hasProgress && onResume && onStartFresh ? (
          <div className="space-y-3">
            <div className="rounded-2xl dark:bg-amber-500/[0.08] bg-amber-50 dark:border-amber-500/20 border border-amber-200 px-4 py-3">
              <p className={`text-sm font-medium ${theme.text.heading} flex items-center gap-2`}>
                <RotateCcw className="w-4 h-4 dark:text-amber-400 text-amber-600 flex-shrink-0" />
                You have an assessment in progress
              </p>
            </div>
            <BigButton onClick={onResume} variant="teal">
              <RotateCcw className="w-5 h-5 mr-2 inline-block" />
              Resume Assessment
            </BigButton>
            <button
              onClick={onStartFresh}
              className={`w-full py-3 px-4 rounded-xl text-sm font-semibold ${theme.anim.transition} dark:text-white/60 text-gray-500 dark:hover:text-white/80 hover:text-gray-700 dark:bg-white/[0.04] bg-gray-50 dark:border-white/[0.08] border border-gray-200 dark:hover:bg-white/[0.07] hover:bg-gray-100`}
            >
              Start Fresh Instead
            </button>
          </div>
        ) : (
          <BigButton onClick={onStart} variant="teal">
            Start Assessment
          </BigButton>
        )}
      </div>

    </div>
  </div>
);

const SportSelectScreen = ({
  selectedSport,
  onSelect,
  onNext,
  onBack,
}: {
  selectedSport: string;
  onSelect: (id: string) => void;
  onNext: () => void;
  onBack: () => void;
}) => (
  <div className={theme.anim.fadeInUp} aria-live="polite">
    <button onClick={onBack} className={`${theme.button.back} mb-4`}>
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
    <h2 className={`text-xl font-bold ${theme.text.heading} mb-1`}>
      Select Sport
    </h2>
    <p className={`${theme.text.body} mb-6 text-sm`}>
      What sport is being played? This helps us tailor the assessment context.
    </p>

    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 mb-6">
      {SPORTS.map((sport) => (
        <button
          key={sport.id}
          type="button"
          onClick={() => onSelect(sport.id)}
          className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-center ${theme.anim.transition} ${
            selectedSport === sport.id
              ? 'dark:bg-sy-teal/[0.12] bg-teal-50 border-sy-teal/40 dark:border-sy-teal/30 ring-1 ring-sy-teal/20'
              : 'dark:bg-white/[0.03] bg-white dark:border-white/[0.08] border-gray-200 dark:hover:bg-white/[0.06] hover:bg-gray-50'
          }`}
        >
          {sport.imageSrc
            ? <img src={sport.imageSrc} alt={sport.label} className="w-7 h-7 object-contain" />
            : <span className="text-2xl leading-none">{sport.icon}</span>
          }
          <span className={`text-xs font-medium leading-tight ${
            selectedSport === sport.id ? theme.text.accent : theme.text.muted
          }`}>
            {sport.label}
          </span>
        </button>
      ))}
    </div>

    <BigButton onClick={onNext} variant="teal" disabled={!selectedSport}>
      Continue
    </BigButton>
  </div>
);

const RedFlagScreen = ({
  selectedFlags,
  onToggleFlag,
  onNext,
  onBack,
}: {
  selectedFlags: string[];
  onToggleFlag: (id: string) => void;
  onNext: () => void;
  onBack: () => void;
}) => (
  <div className={theme.anim.fadeInUp} aria-live="polite">
    <button onClick={onBack} className={`${theme.button.back} mb-4`}>
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
    <h2 className={`text-xl font-bold ${theme.text.heading} mb-1`}>
      Check for Red Flags
    </h2>
    <p className={`${theme.text.body} mb-6 text-sm`}>
      Does the person show ANY of these signs? Tap all that apply.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
      {RED_FLAGS.map((flag) => (
        <button
          key={flag.id}
          onClick={() => onToggleFlag(flag.id)}
          aria-pressed={selectedFlags.includes(flag.id)}
          className={`${theme.selectionItem.base} ${
            selectedFlags.includes(flag.id)
              ? theme.selectionItem.selectedRed
              : theme.selectionItem.unselected
          }`}
        >
          <div className="flex items-center">
            <div
              className={`${theme.selectionItem.checkbox} ${
                selectedFlags.includes(flag.id)
                  ? theme.selectionItem.checkboxRed
                  : theme.selectionItem.checkboxEmpty
              }`}
            >
              {selectedFlags.includes(flag.id) && (
                <CheckCircle className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <p className="font-medium">{flag.label}</p>
            </div>
          </div>
        </button>
      ))}
    </div>

    {selectedFlags.length > 0 ? (
      <BigButton onClick={onNext} variant="danger">
        Red Flag Detected — View Emergency Actions
      </BigButton>
    ) : (
      <BigButton onClick={onNext} variant="teal">
        No Red Flags — Continue Assessment
      </BigButton>
    )}
  </div>
);

const ObservableSignsScreen = ({
  selectedSigns,
  onToggleSign,
  onNext,
  onBack,
}: {
  selectedSigns: string[];
  onToggleSign: (id: string) => void;
  onNext: () => void;
  onBack: () => void;
}) => (
  <div className={theme.anim.fadeInUp} aria-live="polite">
    <button onClick={onBack} className={`${theme.button.back} mb-4`} aria-label="Back to red flags">
      <ArrowLeft className="w-4 h-4 mr-1" />
      Back
    </button>

    <h2 className={`text-xl font-bold ${theme.text.heading} mb-1`}>
      Visible Clues
    </h2>
    <p className={`${theme.text.body} mb-6 text-sm`}>
      Can you see ANY of these signs? Tap all that apply.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
      {OBSERVABLE_SIGNS.map((sign) => (
        <button
          key={sign.id}
          onClick={() => onToggleSign(sign.id)}
          aria-pressed={selectedSigns.includes(sign.id)}
          className={`${theme.selectionItem.base} w-full ${
            selectedSigns.includes(sign.id)
              ? theme.selectionItem.selectedAmber
              : theme.selectionItem.unselected
          }`}
        >
          <div className="flex items-center">
            <div
              className={`${theme.selectionItem.checkbox} ${
                selectedSigns.includes(sign.id)
                  ? theme.selectionItem.checkboxAmber
                  : theme.selectionItem.checkboxEmpty
              }`}
            >
              {selectedSigns.includes(sign.id) && (
                <CheckCircle className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <p className="font-medium">{sign.label}</p>
            </div>
          </div>
        </button>
      ))}
    </div>

    {selectedSigns.length > 0 ? (
      <BigButton onClick={onNext} variant="danger">
        Signs Detected — View Recommendation
      </BigButton>
    ) : (
      <BigButton onClick={onNext} variant="teal">
        No Signs — Continue to Symptoms
      </BigButton>
    )}
  </div>
);

const SelfSymptomsScreen = ({
  selectedSymptoms,
  onToggleSymptom,
  onNext,
  onBack,
}: {
  selectedSymptoms: string[];
  onToggleSymptom: (id: string) => void;
  onNext: () => void;
  onBack: () => void;
}) => (
  <div className={theme.anim.fadeInUp} aria-live="polite">
    <button onClick={onBack} className={`${theme.button.back} mb-4`} aria-label="Back to visible clues">
      <ArrowLeft className="w-4 h-4 mr-1" />
      Back
    </button>

    <h2 className={`text-xl font-bold ${theme.text.heading} mb-1`}>Symptoms</h2>
    <p className={`${theme.text.body} mb-6 text-sm`}>
      Complete all three sections. Tap everything that applies.
    </p>

    {/* ── Section A: Ask the Player ───────────────────────────────── */}
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-500/20">
          <MessageSquare className="w-3.5 h-3.5 text-teal-400" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wider text-teal-400">Ask the Player</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {SELF_SYMPTOMS.map((symptom) => (
          <button
            key={symptom.id}
            onClick={() => onToggleSymptom(symptom.id)}
            aria-pressed={selectedSymptoms.includes(symptom.id)}
            className={`${theme.selectionItem.base} w-full ${
              selectedSymptoms.includes(symptom.id)
                ? theme.selectionItem.selectedAmber
                : theme.selectionItem.unselected
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`${theme.selectionItem.checkbox} flex-shrink-0 ${
                  selectedSymptoms.includes(symptom.id)
                    ? theme.selectionItem.checkboxAmber
                    : theme.selectionItem.checkboxEmpty
                }`}
              >
                {selectedSymptoms.includes(symptom.id) && (
                  <CheckCircle className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-sm leading-snug">{symptom.question}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>

    {/* ── Section B: Observable Symptoms ─────────────────────────── */}
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/20">
          <Eye className="w-3.5 h-3.5 text-amber-400" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wider text-amber-400">Observable Symptoms</span>
        <span className={`text-xs ${theme.text.muted}`}>· For you to determine</span>
      </div>

      {/* Changes in Emotions */}
      <div className="rounded-xl border border-orange-500/25 bg-orange-500/5 p-4 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-semibold text-orange-400">Changes in Emotions</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {EMOTION_SYMPTOMS.map((symptom) => (
            <button
              key={symptom.id}
              onClick={() => onToggleSymptom(symptom.id)}
              aria-pressed={selectedSymptoms.includes(symptom.id)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-left transition-all border ${
                selectedSymptoms.includes(symptom.id)
                  ? "dark:bg-orange-500/20 bg-orange-100 border-orange-400 dark:text-orange-300 text-orange-700"
                  : "dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.04] bg-white dark:text-white text-gray-800 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:border-orange-300 dark:hover:border-orange-500/40"
              }`}
            >
              <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                selectedSymptoms.includes(symptom.id)
                  ? "bg-orange-500 border-orange-400"
                  : "dark:border-white/30 border-gray-300"
              }`}>
                {selectedSymptoms.includes(symptom.id) && <CheckCircle className="w-3 h-3 text-white" />}
              </div>
              {symptom.label}
            </button>
          ))}
        </div>
      </div>

      {/* Changes in Thinking */}
      <div className="rounded-xl border border-violet-500/25 bg-violet-500/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-semibold text-violet-400">Changes in Thinking</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {THINKING_SYMPTOMS.map((symptom) => (
            <button
              key={symptom.id}
              onClick={() => onToggleSymptom(symptom.id)}
              aria-pressed={selectedSymptoms.includes(symptom.id)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-left transition-all border ${
                selectedSymptoms.includes(symptom.id)
                  ? "dark:bg-violet-500/20 bg-violet-100 border-violet-400 dark:text-violet-300 text-violet-700"
                  : "dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.04] bg-white dark:text-white text-gray-800 hover:bg-violet-50 dark:hover:bg-violet-500/10 hover:border-violet-300 dark:hover:border-violet-500/40"
              }`}
            >
              <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                selectedSymptoms.includes(symptom.id)
                  ? "bg-violet-500 border-violet-400"
                  : "dark:border-white/30 border-gray-300"
              }`}>
                {selectedSymptoms.includes(symptom.id) && <CheckCircle className="w-3 h-3 text-white" />}
              </div>
              {symptom.label}
            </button>
          ))}
        </div>
      </div>
    </div>

    {selectedSymptoms.length > 0 ? (
      <BigButton onClick={onNext} variant="danger">
        Symptoms Reported — View Recommendation
      </BigButton>
    ) : (
      <BigButton onClick={onNext} variant="teal">
        No Symptoms — View Result
      </BigButton>
    )}
  </div>
);

// ─── Awareness screen ─────────────────────────────────────────────────────────

const AwarenessScreen = ({
  answers,
  onAnswer,
  onNext,
  onBack,
}: {
  answers: Record<string, 'normal' | 'impaired'>;
  onAnswer: (id: string, value: 'normal' | 'impaired') => void;
  onNext: () => void;
  onBack: () => void;
}) => {
  const answeredCount = Object.keys(answers).length;
  const hasImpaired = Object.values(answers).includes('impaired');

  return (
    <div className={theme.anim.fadeInUp} aria-live="polite">
      <button onClick={onBack} className={`${theme.button.back} mb-4`} aria-label="Back to symptoms">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </button>

      <h2 className={`text-xl font-bold ${theme.text.heading} mb-1`}>Awareness</h2>
      <p className={`${theme.text.body} mb-4 text-sm`}>
        Ask the athlete each question. Tap <span className="font-semibold text-emerald-600 dark:text-emerald-400">Normal</span> or <span className="font-semibold text-red-600 dark:text-red-400">Impaired</span> for each response.
      </p>

      <div className="flex items-start gap-2 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 mb-6">
        <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-yellow-700 dark:text-yellow-400 leading-relaxed">
          Modify each question for the athlete's sport and age. Failure to answer any correctly may suggest a concussion.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {AWARENESS_QUESTIONS.map((q) => {
          const answer = answers[q.id];
          return (
            <div
              key={q.id}
              className={`rounded-2xl border p-4 transition-all ${
                answer === 'impaired'
                  ? 'dark:border-red-500/40 border-red-300 dark:bg-red-500/[0.06] bg-red-50'
                  : answer === 'normal'
                  ? 'dark:border-emerald-500/40 border-emerald-300 dark:bg-emerald-500/[0.06] bg-emerald-50'
                  : 'dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.04] bg-gray-50'
              }`}
            >
              <p className={`font-medium text-sm mb-3 ${theme.text.heading}`}>{q.label}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onAnswer(q.id, 'normal')}
                  aria-pressed={answer === 'normal'}
                  className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold border transition-all ${
                    answer === 'normal'
                      ? 'bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-500/20'
                      : 'dark:border-white/[0.12] border-gray-300 dark:bg-white/[0.04] bg-white dark:text-white text-gray-700 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:border-emerald-400 dark:hover:border-emerald-500/50'
                  }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  Normal
                </button>
                <button
                  onClick={() => onAnswer(q.id, 'impaired')}
                  aria-pressed={answer === 'impaired'}
                  className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold border transition-all ${
                    answer === 'impaired'
                      ? 'bg-red-500 border-red-500 text-white shadow-md shadow-red-500/20'
                      : 'dark:border-white/[0.12] border-gray-300 dark:bg-white/[0.04] bg-white dark:text-white text-gray-700 hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-400 dark:hover:border-red-500/50'
                  }`}
                >
                  <AlertTriangle className="w-4 h-4" />
                  Impaired
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {hasImpaired ? (
        <BigButton onClick={onNext} variant="danger">
          Impaired Response — View Recommendation
        </BigButton>
      ) : answeredCount === AWARENESS_QUESTIONS.length ? (
        <BigButton onClick={onNext} variant="success">
          All Normal — View Result
        </BigButton>
      ) : (
        <BigButton onClick={onNext} variant="teal">
          Continue to Result
        </BigButton>
      )}
    </div>
  );
};

// ─── Assessment summary ──────────────────────────────────────────────────────

type AssessmentSummaryProps = {
  subjectName: string;
  sport: string;
  selectedRedFlags: string[];
  selectedObservableSigns: string[];
  selectedSelfSymptoms: string[];
  awarenessAnswers: Record<string, 'normal' | 'impaired'>;
  outcome: OutcomeType;
  startedAt: Date | null;
  completedAt: Date | null;
  geolocation?: { lat: number; lng: number; address?: string | null } | null;
  sentToEmail?: string | null;
};

const AssessmentSummary = ({
  subjectName,
  sport,
  selectedRedFlags,
  selectedObservableSigns,
  selectedSelfSymptoms,
  awarenessAnswers,
  outcome,
  startedAt,
  completedAt,
  geolocation,
  sentToEmail,
}: AssessmentSummaryProps) => {
  const sportLabel = SPORTS.find((s) => s.id === sport)?.label ?? sport;
  const redFlagLabels = selectedRedFlags.map((id) => RED_FLAGS.find((f) => f.id === id)?.label ?? id);
  const signLabels = selectedObservableSigns.map((id) => OBSERVABLE_SIGNS.find((s) => s.id === id)?.label ?? id);
  const symptomLabels = selectedSelfSymptoms.map((id) => [...SELF_SYMPTOMS, ...EMOTION_SYMPTOMS, ...THINKING_SYMPTOMS].find((s) => s.id === id)?.label ?? id);
  const awarenessEntries = AWARENESS_QUESTIONS.filter((q) => awarenessAnswers[q.id]).map((q) => ({ label: q.label, answer: awarenessAnswers[q.id] }));

  const outcomeData = OUTCOMES[outcome];
  const duration =
    startedAt && completedAt
      ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
      : null;

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  return (
    <div className={`${theme.card.paddedMuted} mt-5 md:mt-0 ${theme.anim.fadeInUp}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={theme.icon.tealSm}>
          <ClipboardList className={`w-4 h-4 ${theme.text.accent}`} />
        </div>
        <span className={theme.text.accentLabel}>Incident Summary</span>
      </div>

      <div className="space-y-3 text-sm">
        {/* Player & sport */}
        {subjectName.trim() && (
          <div className="flex justify-between">
            <span className={theme.text.muted}>Player</span>
            <span className={`${theme.text.heading} font-medium`}>{subjectName.trim()}</span>
          </div>
        )}
        {sport && (
          <div className="flex justify-between">
            <span className={theme.text.muted}>Sport</span>
            <span className={`${theme.text.heading} font-medium`}>{sportLabel}</span>
          </div>
        )}

        {/* Outcome */}
        <div className="flex justify-between items-center">
          <span className={theme.text.muted}>Outcome</span>
          <span className="font-semibold text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${outcomeData.color}20`, color: outcomeData.color }}>
            {outcomeData.title}
          </span>
        </div>

        {/* Timing */}
        {startedAt && (
          <div className="flex justify-between">
            <span className={theme.text.muted}>Time</span>
            <span className={`${theme.text.heading} font-medium`}>
              {startedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              {duration !== null && ` (${formatDuration(duration)})`}
            </span>
          </div>
        )}

        {/* Location */}
        {geolocation?.address && (
          <div className="flex justify-between">
            <span className={theme.text.muted}>Location</span>
            <span className={`${theme.text.heading} font-medium text-right max-w-[60%]`}>{geolocation.address}</span>
          </div>
        )}

        {/* Email — shown after report is sent */}
        {sentToEmail && (
          <div className={`flex justify-between items-center ${theme.anim.fadeInUp}`}>
            <span className={theme.text.muted}>Report sent to</span>
            <span className={`${theme.text.heading} font-medium text-right max-w-[60%] truncate`}>{sentToEmail}</span>
          </div>
        )}

        {/* Findings divider */}
        <div className={`border-t ${theme.text.muted} pt-3 mt-3`}>
          <span className={`${theme.text.heading} font-semibold text-xs uppercase tracking-wider`}>Findings</span>
        </div>

        {/* Red flags */}
        {redFlagLabels.length > 0 && (
          <div>
            <span className="font-medium text-red-500 text-xs">Red Flags ({redFlagLabels.length})</span>
            <ul className={`mt-1 space-y-1 ${theme.text.subheading}`}>
              {redFlagLabels.map((label) => (
                <li key={label} className="flex items-start gap-1.5">
                  <span className="text-red-500 mt-0.5">&#x2022;</span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Observable signs */}
        {signLabels.length > 0 && (
          <div>
            <span className="font-medium text-amber-500 text-xs">Visible Clues ({signLabels.length})</span>
            <ul className={`mt-1 space-y-1 ${theme.text.subheading}`}>
              {signLabels.map((label) => (
                <li key={label} className="flex items-start gap-1.5">
                  <span className="text-amber-500 mt-0.5">&#x2022;</span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Symptoms */}
        {symptomLabels.length > 0 && (
          <div>
            <span className="font-medium text-amber-500 text-xs">Reported Symptoms ({symptomLabels.length})</span>
            <ul className={`mt-1 space-y-1 ${theme.text.subheading}`}>
              {symptomLabels.map((label) => (
                <li key={label} className="flex items-start gap-1.5">
                  <span className="text-amber-500 mt-0.5">&#x2022;</span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Awareness */}
        {awarenessEntries.length > 0 && (
          <div>
            <span className="font-medium text-xs" style={{ color: awarenessEntries.some(e => e.answer === 'impaired') ? '#EF4444' : '#10B981' }}>
              Awareness ({awarenessEntries.length}/{AWARENESS_QUESTIONS.length})
            </span>
            <ul className={`mt-1 space-y-1 ${theme.text.subheading}`}>
              {awarenessEntries.map(({ label, answer }) => (
                <li key={label} className="flex items-start gap-1.5">
                  <span className={`mt-0.5 font-bold ${answer === 'impaired' ? 'text-red-500' : 'text-emerald-500'}`}>&#x2022;</span>
                  <span className="flex-1">{label}</span>
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    answer === 'impaired'
                      ? 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'
                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                  }`}>
                    {answer === 'impaired' ? 'Impaired' : 'Normal'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* No findings */}
        {redFlagLabels.length === 0 && signLabels.length === 0 && symptomLabels.length === 0 && awarenessEntries.length === 0 && (
          <p className={`${theme.text.muted} italic`}>No red flags, visible clues, or symptoms detected.</p>
        )}
      </div>
    </div>
  );
};

const OutcomeScreen = ({
  outcome,
  subjectName,
  sport,
  assessmentState,
  onRestart,
  onBack,
  beforeRestart,
  sentToEmail,
  isPublic,
  isLoggedIn,
  onRegister,
  emailSent,
  emailExistsInDb,
}: {
  outcome: OutcomeType;
  subjectName: string;
  sport: string;
  assessmentState: AssessmentState;
  onRestart: () => void;
  onBack?: () => void;
  beforeRestart?: React.ReactNode;
  sentToEmail?: string | null;
  isPublic?: boolean;
  isLoggedIn?: boolean;
  onRegister?: () => void;
  emailSent?: boolean;
  emailExistsInDb?: boolean | null;
}) => {
  const outcomeData = OUTCOMES[outcome];

  return (
    <div className={theme.anim.fadeInUp}>
      {onBack && (
        <button onClick={onBack} className={`${theme.button.back} mb-4`}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}
      <div className="flex flex-col md:flex-row md:gap-6">
        {/* Left column: Outcome card + Emergency button */}
        <div className="md:w-1/2">
          {/* Outcome result card */}
          <div
            className="rounded-2xl border-2 p-6 mb-5"
            style={{
              borderColor: `${outcomeData.color}50`,
              backgroundColor: `${outcomeData.color}0D`,
            }}
          >
            {/* Outcome badge */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${outcomeData.color}20` }}
              >
                <span className="text-lg font-black" style={{ color: outcomeData.color }}>!</span>
              </div>
              <h2 className="text-xl font-bold leading-tight" style={{ color: outcomeData.color }}>
                {outcomeData.title}
              </h2>
            </div>

            <p className={`${theme.text.subheading} mb-5 leading-relaxed`}>{outcomeData.description}</p>

            {/* Action steps */}
            <div className="space-y-3">
              {outcomeData.actions.map((action, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white mt-0.5"
                    style={{ backgroundColor: outcomeData.color }}
                  >
                    {i + 1}
                  </span>
                  <p className={`${theme.text.subheading} leading-snug text-sm`}>{action}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Emergency call button — prominent */}
          {outcome === "emergency" && (
            <div className="mb-5">
              <EmergencyButton />
            </div>
          )}

          <BigButton onClick={onRestart} variant="default">
            <div className="flex items-center justify-center gap-2">
              <RotateCcw className="w-4 h-4" />
              Start New Assessment
            </div>
          </BigButton>

          <p className={`mt-4 text-xs ${theme.text.veryMuted} text-center leading-relaxed`}>
            This tool is not a medical device. All suspected concussions should be assessed by a
            qualified medical professional.
          </p>
        </div>

        {/* Right column: Summary + Save */}
        <div className="md:w-1/2">
          {/* Assessment summary */}
          <AssessmentSummary
            subjectName={subjectName}
            sport={sport}
            selectedRedFlags={assessmentState.selectedRedFlags}
            selectedObservableSigns={assessmentState.selectedObservableSigns}
            selectedSelfSymptoms={assessmentState.selectedSelfSymptoms}
            awarenessAnswers={assessmentState.awarenessAnswers}
            outcome={outcome}
            startedAt={assessmentState.startedAt}
            completedAt={assessmentState.completedAt}
            geolocation={assessmentState.geolocation}
            sentToEmail={sentToEmail}
          />

          {/* CTA — logged-in users go to dashboard, anonymous users see signup prompt */}
          {isLoggedIn ? (
            <div className={`mt-4 rounded-2xl border dark:border-sy-teal/20 border-teal-200 dark:bg-sy-teal/[0.06] bg-teal-50/50 px-5 py-4 flex items-center justify-between gap-4 ${theme.anim.fadeInUp}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl dark:bg-sy-teal/[0.15] bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className={`w-5 h-5 ${theme.text.accent}`} />
                </div>
                <div className="min-w-0">
                  <p className={`${theme.text.heading} font-semibold text-sm`}>Manage your assessments</p>
                  <p className={`${theme.text.muted} text-xs`}>View history, track recovery &amp; more</p>
                </div>
              </div>
              <Link
                to="/assessments"
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sy-teal dark:bg-sy-teal text-sy-navy font-bold text-sm transition-opacity hover:opacity-90`}
              >
                View History <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            isPublic && emailSent && (
              emailExistsInDb
                ? (
                  // Existing user — prompt to sign in
                  <div className={`mt-4 rounded-2xl border dark:border-teal-500/20 border-teal-200 dark:bg-teal-500/[0.06] bg-teal-50/60 px-5 py-4 flex items-center justify-between gap-4 ${theme.anim.fadeInUp}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl dark:bg-teal-500/[0.15] bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <User className={`w-5 h-5 ${theme.text.accent}`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`${theme.text.heading} font-semibold text-sm`}>You already have an account</p>
                        <p className={`${theme.text.muted} text-xs`}>Sign in to view your assessment log</p>
                      </div>
                    </div>
                    <a
                      href="/login"
                      className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sy-teal text-sy-navy font-bold text-sm transition-opacity hover:opacity-90"
                    >
                      Sign In <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                )
                : onRegister && (
                  // New user — show signup container
                  <div className={`mt-4 rounded-2xl overflow-hidden border-2 border-emerald-500/30 dark:border-emerald-400/20 ${theme.anim.fadeInUp}`}>
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-700 dark:to-teal-700 px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                          <Shield className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-base leading-tight">
                            Track &amp; manage this assessment
                          </h3>
                          <p className="text-emerald-100 text-xs mt-0.5">
                            Free account — set up in 30 seconds
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="dark:bg-emerald-950/30 bg-emerald-50/60 px-5 py-4 space-y-3">
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2.5">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span className={`${theme.text.subheading} text-sm`}>Full assessment log &amp; case management</span>
                        </li>
                        <li className="flex items-center gap-2.5">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span className={`${theme.text.subheading} text-sm`}>Return-to-play tracking &amp; aftercare</span>
                        </li>
                        <li className="flex items-center gap-2.5">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span className={`${theme.text.subheading} text-sm`}>Notify parents &amp; guardians automatically</span>
                        </li>
                      </ul>
                      <button
                        onClick={onRegister}
                        className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400 text-white font-bold text-base transition-all shadow-lg shadow-emerald-600/25 hover:shadow-emerald-500/30"
                      >
                        <UserPlus className="w-5 h-5" />
                        Create Free Account
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
            )
          )}

          {beforeRestart}
        </div>
      </div>
    </div>
  );
};

// ─── Assessor name capture ────────────────────────────────────────────────────

const AssessorNameCapture = ({
  assessorName,
  onAssessorNameChange,
  onSave,
}: {
  assessorName: string;
  onAssessorNameChange: (v: string) => void;
  onSave: () => void;
}) => (
  <div className={`mt-6 ${theme.card.paddedMuted} ${theme.anim.fadeInUp}`}>
    <div className="flex items-center gap-2 mb-3">
      <div className={theme.icon.tealSm}>
        <User className={`w-4 h-4 ${theme.text.accent}`} />
      </div>
      <span className={theme.text.accentLabel}>For Your Records</span>
    </div>

    <h3 className={`text-lg font-bold ${theme.text.heading} mb-2`}>
      Want to know what happens next?
    </h3>
    <p className={`${theme.text.body} text-sm mb-4`}>
      Enter your name to personalise this incident report — so you can share it with
      club officials, medical staff, or parents with confidence.
    </p>

    <input
      type="text"
      value={assessorName}
      onChange={(e) => onAssessorNameChange(e.target.value.slice(0, 100))}
      placeholder="Your name (e.g. Coach Sarah)"
      className={`${theme.input.base} mb-3`}
    />

    <button className={`${theme.button.medBase} ${theme.button.medTeal} mb-2`} onClick={onSave}>
      Save Assessment
    </button>

    <button onClick={onSave} className={theme.button.ghost}>
      Save without a name
    </button>
  </div>
);

// ─── Org banner (shown when org sports mapping exists) ──────────────────────

const OrgBanner = ({ orgName, sportLabel }: { orgName: string; sportLabel: string }) => (
  <div className={`mb-6 rounded-xl border dark:border-sy-teal/20 border-teal-200 dark:bg-sy-teal/[0.06] bg-teal-50/50 px-4 py-3 flex items-center gap-3 ${theme.anim.fadeInUp}`}>
    <div className="w-9 h-9 rounded-lg dark:bg-sy-teal/[0.15] bg-teal-100 flex items-center justify-center flex-shrink-0">
      <Building2 className={`w-5 h-5 ${theme.text.accent}`} />
    </div>
    <div className="min-w-0">
      <p className={`${theme.text.heading} font-semibold text-sm truncate`}>{orgName}</p>
      <p className={`${theme.text.muted} text-xs`}>{sportLabel}</p>
    </div>
  </div>
);

// ─── Post-assessment actions ────────────────────────────────────────────────

// ─── Email copy form ───────────────────────────────────────────────────────

const EmailCopyForm = ({
  onSubmit,
  onCancel,
  showBackButton = false,
  isSending = false,
  heading = 'Get your assessment report',
  description = "Enter your email and we'll send you the assessment summary.",
  buttonLabel = 'Send Report and Track',
}: {
  onSubmit: (email: string) => void;
  onCancel: () => void;
  showBackButton?: boolean;
  isSending?: boolean;
  heading?: string;
  description?: string;
  buttonLabel?: string;
}) => {
  const [email, setEmail] = useState('');
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <div className={`mt-6 ${theme.card.paddedMuted} ${theme.anim.fadeInUp}`}>
      {showBackButton && (
        <button onClick={onCancel} className={`${theme.button.back} mb-3`}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}
      <h3 className={`text-lg font-bold ${theme.text.heading} mb-2`}>
        {heading}
      </h3>
      <p className={`${theme.text.body} text-sm mb-4`}>
        {description}
      </p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value.slice(0, 200))}
        placeholder="email@example.com"
        className={`${theme.input.base} mb-3`}
        disabled={isSending}
      />
      <button
        className={`${theme.button.medBase} ${theme.button.medTeal}`}
        onClick={() => onSubmit(email)}
        disabled={!isValid || isSending}
      >
        {isSending ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Sending...
          </span>
        ) : (
          buttonLabel
        )}
      </button>
    </div>
  );
};

// ─── Club sharing screen ───────────────────────────────────────────────────

type ClubSharingScreenProps = {
  orgId: string;
  orgName: string;
  sessionId: string;
  incidentId?: string | null;
  userId?: string | null;
  userGeo?: { lat: number; lng: number } | null;
  onComplete: () => void;
  onCancel: () => void;
};

const ClubSharingScreen = ({
  orgId,
  orgName,
  sessionId,
  incidentId,
  userId,
  userGeo,
  onComplete,
  onCancel,
}: ClubSharingScreenProps) => {
  const [locations, setLocations] = useState<OrgLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClub, setSelectedClub] = useState<OrgLocation | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    getOrgLocations(orgId).then((result) => {
      setLocations(result);
      // Auto-select nearest club if user geo is available
      if (userGeo && result.length > 0) {
        const nearest = findNearestOrgLocation(result, userGeo.lat, userGeo.lng);
        if (nearest) setSelectedClub(nearest);
      }
      setLoading(false);
    });
  }, [orgId, userGeo]);

  const filteredLocations = locations.filter((loc) => {
    const q = searchQuery.toLowerCase();
    return (
      loc.venue_name.toLowerCase().includes(q) ||
      (loc.address?.city?.toLowerCase().includes(q) ?? false) ||
      (loc.address?.state?.toLowerCase().includes(q) ?? false)
    );
  });

  const handleShare = async () => {
    if (!selectedClub) return;
    setSharing(true);
    const success = await shareAssessmentWithClub({
      incident_id: incidentId,
      session_id: sessionId,
      org_id: orgId,
      club_id: selectedClub.id,
      shared_by_user_id: userId,
    });
    setSharing(false);
    if (success) {
      setShared(true);
    }
  };

  if (shared) {
    return (
      <div className={`mt-6 ${theme.card.paddedMuted} ${theme.anim.fadeInUp}`}>
        <div className="text-center py-4">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-emerald-500" />
          </div>
          <h3 className={`text-lg font-bold ${theme.text.heading} mb-2`}>Shared with {selectedClub?.venue_name}</h3>
          <p className={`${theme.text.body} text-sm mb-4`}>
            Your assessment has been shared with your club. They can view the incident details for their records.
          </p>
          <button onClick={onComplete} className={`${theme.button.medBase} ${theme.button.medTeal}`}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-6 ${theme.card.paddedMuted} ${theme.anim.fadeInUp}`}>
      <button onClick={onCancel} className={`${theme.button.back} mb-3`}>
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center gap-2 mb-4">
        <div className={theme.icon.tealSm}>
          <Building2 className={`w-4 h-4 ${theme.text.accent}`} />
        </div>
        <span className={theme.text.accentLabel}>Share with your club</span>
      </div>

      <p className={`${theme.text.body} text-sm mb-4`}>
        Select your club from the {orgName} club list.
      </p>

      {/* Search */}
      <div className="relative mb-4">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.text.muted}`} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value.slice(0, 100))}
          placeholder="Search clubs..."
          className={`${theme.input.base} pl-9`}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className={`w-6 h-6 ${theme.text.accent} ${theme.anim.spin}`} />
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
          {filteredLocations.map((loc) => (
            <button
              key={loc.id}
              onClick={() => setSelectedClub(loc)}
              className={`w-full text-left px-4 py-3 rounded-xl border ${theme.anim.transition} ${
                selectedClub?.id === loc.id
                  ? 'dark:bg-sy-teal/[0.12] bg-teal-50 border-sy-teal/40 dark:border-sy-teal/30 ring-1 ring-sy-teal/20'
                  : 'dark:bg-white/[0.03] bg-white dark:border-white/[0.08] border-gray-200 dark:hover:bg-white/[0.06] hover:bg-gray-50'
              }`}
            >
              <p className={`font-medium text-sm ${selectedClub?.id === loc.id ? theme.text.accent : theme.text.heading}`}>
                {loc.venue_name}
              </p>
              <p className={`text-xs ${theme.text.muted}`}>
                {[loc.address?.city, loc.address?.state].filter(Boolean).join(', ')}
              </p>
            </button>
          ))}
          {filteredLocations.length === 0 && (
            <p className={`${theme.text.muted} text-sm text-center py-4`}>
              No clubs found{searchQuery ? ` matching "${searchQuery}"` : ''}.
            </p>
          )}
        </div>
      )}

      <button
        className={`${theme.button.medBase} ${theme.button.medTeal}`}
        onClick={handleShare}
        disabled={!selectedClub || sharing}
      >
        {sharing ? (
          <span className="flex items-center gap-2">
            <Loader2 className={`w-4 h-4 ${theme.anim.spin}`} />
            Sharing...
          </span>
        ) : (
          `Share with ${selectedClub?.venue_name ?? 'selected club'}`
        )}
      </button>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const INITIAL_STATE: AssessmentState = {
  currentStep: "sport-select",
  selectedRedFlags: [],
  selectedObservableSigns: [],
  selectedSelfSymptoms: [],
  awarenessAnswers: {},
  outcome: null,
  startedAt: null,
  completedAt: null,
  geolocation: null,
  stepTimestamps: {},
};

export const AssessmentPage = () => {
  const anonymousSession = useAnonymousSessionOptional();
  const { orgSlug } = useParams<{ orgSlug?: string }>();

  const [state, setState] = useState<AssessmentState>(INITIAL_STATE);
  const [started, setStarted] = useState(false);
  const [subjectName, setSubjectName] = useState<string>("");
  const [selectedSport, setSelectedSport] = useState<string>("");

  // Track in-progress assessment for resume flow
  const savedProgressRef = useRef<{ state: AssessmentState; sport: string } | null>(null);
  const hasProgress = savedProgressRef.current !== null;
  const [isSaving, setIsSaving] = useState(false);
  const [incidentRecord, setIncidentRecord] = useState<IncidentRecord | null>(null);
  const [saveError, setSaveError] = useState(false);
  const hasSavedRef = useRef(false);
  const [assessorName, setAssessorName] = useState<string>("");
  const [assessorNameReady, setAssessorNameReady] = useState(false);

  // Post-assessment action state
  type PostAction = 'none' | 'club_share';
  const [postAction, setPostAction] = useState<PostAction>('none');
  const [emailSent, setEmailSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [emailExistsInDb, setEmailExistsInDb] = useState<boolean | null>(null);

  // Send to club (org email) state
  const [orgEmail, setOrgEmail] = useState<string | null>(null);
  const [clubEmailSending, setClubEmailSending] = useState(false);
  const [clubEmailSent, setClubEmailSent] = useState(false);
  const [clubEmailError, setClubEmailError] = useState<string | null>(null);

  // Share panel for logged-in users at assessment end
  const [showSharePanel, setShowSharePanel] = useState(false);

  // Track whether a real (non-anonymous) user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  useEffect(() => {
    const check = (user: { is_anonymous?: boolean; email?: string } | null) => {
      setIsLoggedIn(!!user && user.is_anonymous !== true && !!user.email);
    };
    supabase.auth.getSession().then(({ data: { session } }) => check(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => check(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // If no session venueData (direct navigation, not via QR), resolve org from URL slug or system default
  const [fallbackOrgData, setFallbackOrgData] = useState<{ org_id: string; org_name: string; sports: Record<string, string>; clubs_enabled: boolean } | null>(null);

  useEffect(() => {
    if (anonymousSession?.venueData?.org) return;
    // orgSlug present → resolve that org (e.g. /assessment/judo → Judo Australia)
    // no slug → fall back to is_default location (generic public entry)
    const lookup = orgSlug ? getPublicVenue(orgSlug) : getDefaultOrgVenue();
    lookup.then((venue) => {
      if (!venue) return;
      const meta = (venue.metadata ?? {}) as Record<string, unknown>;
      const orgName = meta.org_name as string | undefined;
      if (orgName) {
        setFallbackOrgData({
          org_id: venue.org_id,
          org_name: orgName,
          sports: (meta.sports ?? {}) as Record<string, string>,
          clubs_enabled: meta.clubs_enabled === true,
        });
      }
    });
  }, [anonymousSession?.venueData?.org, orgSlug]);

  // Org-aware sport auto-selection (session data takes priority, fallback for direct nav)
  const orgData = anonymousSession?.venueData?.org ?? fallbackOrgData;
  const orgSports = orgData?.sports ?? {};
  const hasOrgSports = Object.keys(orgSports).length > 0;
  const orgSportId = hasOrgSports ? Object.keys(orgSports)[0] : null;
  const orgSportLabel = hasOrgSports ? Object.values(orgSports)[0] : null;

  // Fetch org contact email for "Email my club" post-assessment option
  useEffect(() => {
    if (!orgData?.org_id) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    fetch(`${apiUrl}/api/charlie/assessment/org-email?org_id=${encodeURIComponent(orgData.org_id)}`)
      .then((r) => r.json())
      .then((body: { email: string | null }) => { setOrgEmail(body.email ?? null); })
      .catch(() => { /* non-blocking — no org email = button hidden */ });
  }, [orgData?.org_id]);

  // ── Geolocation (F-013) ──────────────────────────────────────────────────────
  const {
    coordinates: geoCoordinates,
    address: geoAddress,
    permissionState: geoPermissionState,
    isLoading: geoIsLoading,
    requestLocation,
  } = useGeolocation();
  const [manualAddress, setManualAddress] = useState<string>("");
  const [locationSkipped, setLocationSkipped] = useState(false);

  // Whether the session already provides a pre-captured location (QR venue access)
  const hasSessionGeo = !!(
    anonymousSession?.venueData?.geolocation || anonymousSession?.userGeolocation
  );

  // ── Session persistence: load on mount ──────────────────────────────────────
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(ASSESSMENT_SESSION_KEY);
      if (!stored) return;
      const parsed: { state: AssessmentState; subjectName: string; selectedSport?: string; savedAt: string; orgSlug?: string | null } =
        JSON.parse(stored);
      const savedAt = new Date(parsed.savedAt);
      if (Date.now() - savedAt.getTime() > ASSESSMENT_SESSION_TIMEOUT_MS) {
        sessionStorage.removeItem(ASSESSMENT_SESSION_KEY);
        return;
      }
      // Discard session if it was saved under a different org context
      if ((parsed.orgSlug ?? null) !== (orgSlug ?? null)) {
        sessionStorage.removeItem(ASSESSMENT_SESSION_KEY);
        return;
      }
      // Rehydrate Date objects
      setState({
        ...parsed.state,
        startedAt: parsed.state.startedAt ? new Date(parsed.state.startedAt) : null,
        completedAt: parsed.state.completedAt ? new Date(parsed.state.completedAt) : null,
      });
      setSubjectName(parsed.subjectName);
      if (parsed.selectedSport) setSelectedSport(parsed.selectedSport);
      setStarted(true);
    } catch {
      sessionStorage.removeItem(ASSESSMENT_SESSION_KEY);
    }
  }, [orgSlug]);

  // ── Session persistence: save on state change ────────────────────────────────
  useEffect(() => {
    if (!started || state.currentStep === "outcome") return;
    try {
      sessionStorage.setItem(
        ASSESSMENT_SESSION_KEY,
        JSON.stringify({ state, subjectName, selectedSport, savedAt: new Date().toISOString(), orgSlug: orgSlug ?? null })
      );
    } catch {
      // Storage full — ignore
    }
  }, [state, started, subjectName, selectedSport]);

  // ── Scroll to top on step change ────────────────────────────────────────────
  useEffect(() => {
    if (started) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [state.currentStep]);

  // ── Navigation warning while assessment is in progress ──────────────────────
  useEffect(() => {
    if (!started || state.currentStep === "outcome") return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Assessment in progress. Are you sure you want to leave?";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [started, state.currentStep]);

  // ── Geolocation from anonymous session ──────────────────────────────────────
  useEffect(() => {
    if (anonymousSession) {
      const geo =
        anonymousSession.userGeolocation || anonymousSession.venueData?.geolocation || null;
      if (geo) {
        setState((prev) => ({ ...prev, geolocation: geo }));
      }
    }
  }, [anonymousSession]);

  // ── Keyboard: Backspace to go back ──────────────────────────────────────────
  const handleBack = useCallback(() => {
    if (state.currentStep === "red-flags") {
      if (hasOrgSports) {
        // Sport was auto-selected — no sport-select screen existed, go back to start cleanly.
        // Don't save to savedProgressRef (user is at step 1, nothing meaningful to resume).
        setStarted(false);
        setState(INITIAL_STATE);
      } else {
        setState((prev) => ({ ...prev, currentStep: "sport-select" }));
      }
    } else if (state.currentStep === "observable-signs") {
      setState((prev) => ({ ...prev, currentStep: "red-flags" }));
    } else if (state.currentStep === "symptoms") {
      setState((prev) => ({ ...prev, currentStep: "observable-signs" }));
    } else if (state.currentStep === "awareness") {
      setState((prev) => ({ ...prev, currentStep: "symptoms" }));
    }
  }, [state.currentStep, hasOrgSports]);

  useEffect(() => {
    if (!started || state.currentStep === "outcome") return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Backspace" && e.target === document.body) {
        handleBack();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [started, state.currentStep, handleBack]);

  // ── Incident saving ──────────────────────────────────────────────────────────
  const saveIncident = useCallback(
    async (outcomeValue: OutcomeType, currentState: AssessmentState) => {
      if (hasSavedRef.current) return;
      hasSavedRef.current = true;
      setIsSaving(true);
      setSaveError(false);

      // Clear in-progress session now that assessment is complete
      sessionStorage.removeItem(ASSESSMENT_SESSION_KEY);

      try {
        let userId: string | null = null;
        let authenticatedEmail: string | null = null;
        const { data: userData } = await supabase.auth.getUser();
        userId = userData?.user?.id ?? null;
        // Capture email only for real (non-anonymous) auth users
        authenticatedEmail = userData?.user?.email ?? null;

        if (!userId) {
          try {
            const { data: anonData } = await supabase.auth.signInAnonymously();
            userId = anonData?.user?.id ?? null;
          } catch (error) {
            console.warn("[AssessmentPage] signInAnonymously failed, using context fallback:", error);
          }
        }

        if (!userId) {
          userId = anonymousSession?.anonUserId ?? null;
        }

        // Determine geolocation: session pre-captured > browser hook > null
        const geoForIncident =
          currentState.geolocation ??
          (geoCoordinates
            ? { ...geoCoordinates, address: geoAddress }
            : null);

        // Manual address is stored in metadata when no GPS coordinates are available
        const manualAddressTrimmed = manualAddress.trim() || null;

        const record = await createIncident({
          session_id: anonymousSession?.sessionId ?? crypto.randomUUID(),
          venue_id: anonymousSession?.venueId ?? null,
          user_id: userId,
          incident_type: "assessment",
          assessment_data: {
            selected_red_flags: currentState.selectedRedFlags,
            selected_observable_signs: currentState.selectedObservableSigns,
            selected_self_symptoms: currentState.selectedSelfSymptoms,
            awareness_answers: currentState.awarenessAnswers,
            started_at: currentState.startedAt?.toISOString() ?? "",
            completed_at: new Date().toISOString(),
            step_timestamps: {
              red_flags_completed_at: currentState.stepTimestamps.redFlagsCompletedAt,
              observable_signs_completed_at: currentState.stepTimestamps.observableSignsCompletedAt,
              self_symptoms_completed_at: currentState.stepTimestamps.selfSymptomsCompletedAt,
              awareness_completed_at: currentState.stepTimestamps.awarenessCompletedAt,
            },
          },
          red_flags_detected: currentState.selectedRedFlags.length > 0,
          outcome: outcomeValue,
          geolocation: geoForIncident,
          subject_name: subjectName.trim() || null,
          metadata: {
            ...(assessorName.trim() ? { assessor_name: assessorName.trim() } : {}),
            ...(selectedSport ? { sport: selectedSport } : {}),
            ...(manualAddressTrimmed && !geoForIncident
              ? { location_text: manualAddressTrimmed }
              : {}),
            // Store email for signed-in users so assessment history filtering works
            ...(authenticatedEmail ? { contact_email: authenticatedEmail } : {}),
          },
        });

        setIncidentRecord(record);
      } catch (error) {
        console.error("[AssessmentPage] Failed to save incident:", error);
        setSaveError(true);
        hasSavedRef.current = false;
      } finally {
        setIsSaving(false);
      }
    },
    [anonymousSession, subjectName, selectedSport, assessorName, geoCoordinates, geoAddress, manualAddress]
  );

  // ── Auto-save incident when outcome is determined ───────────────────────────
  useEffect(() => {
    if (state.outcome && !hasSavedRef.current) {
      saveIncident(state.outcome, state);
    }
  }, [state.outcome]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step transition handlers ─────────────────────────────────────────────────

  const handleStart = () => {
    savedProgressRef.current = null;
    setStarted(true);
    // If org has sports mapped, auto-select sport and skip sport-select step
    if (hasOrgSports && orgSportId) {
      setSelectedSport(orgSportId);
      setState((prev) => ({ ...prev, startedAt: new Date(), currentStep: "red-flags" }));
    } else {
      setState((prev) => ({ ...prev, startedAt: new Date() }));
    }
  };

  const handleResume = () => {
    if (!savedProgressRef.current) return;
    const { state: savedState, sport } = savedProgressRef.current;
    setState(savedState);
    setSelectedSport(sport);
    savedProgressRef.current = null;
    setStarted(true);
  };

  const handleStartFresh = () => {
    savedProgressRef.current = null;
    setSubjectName("");
    setSelectedSport("");
    setAssessorName("");
    setAssessorNameReady(false);
    setIncidentRecord(null);
    setSaveError(false);
    setIsSaving(false);
    hasSavedRef.current = false;
    setState(INITIAL_STATE);
    setStarted(true);
    // If org has sports mapped, auto-select sport and skip sport-select step
    if (hasOrgSports && orgSportId) {
      setSelectedSport(orgSportId);
      setState((prev) => ({ ...prev, startedAt: new Date(), currentStep: "red-flags" }));
    } else {
      setState((prev) => ({ ...prev, startedAt: new Date() }));
    }
  };

  const handleSportNext = () => {
    setState((prev) => ({ ...prev, currentStep: "red-flags" }));
  };

  const handleToggleRedFlag = (id: string) => {
    setState((prev) => ({
      ...prev,
      selectedRedFlags: prev.selectedRedFlags.includes(id)
        ? prev.selectedRedFlags.filter((f) => f !== id)
        : [...prev.selectedRedFlags, id],
    }));
  };

  const handleToggleObservableSign = (id: string) => {
    setState((prev) => ({
      ...prev,
      selectedObservableSigns: prev.selectedObservableSigns.includes(id)
        ? prev.selectedObservableSigns.filter((s) => s !== id)
        : [...prev.selectedObservableSigns, id],
    }));
  };

  const handleToggleSelfSymptom = (id: string) => {
    setState((prev) => ({
      ...prev,
      selectedSelfSymptoms: prev.selectedSelfSymptoms.includes(id)
        ? prev.selectedSelfSymptoms.filter((s) => s !== id)
        : [...prev.selectedSelfSymptoms, id],
    }));
  };

  const handleRedFlagNext = () => {
    const now = new Date().toISOString();
    const timestamps = { ...state.stepTimestamps, redFlagsCompletedAt: now };
    if (state.selectedRedFlags.length > 0) {
      setState({
        ...state,
        currentStep: "outcome",
        outcome: "emergency",
        completedAt: new Date(),
        stepTimestamps: timestamps,
      });
    } else {
      setState((prev) => ({
        ...prev,
        currentStep: "observable-signs",
        stepTimestamps: timestamps,
      }));
    }
  };

  const handleObservableSignsNext = () => {
    const now = new Date().toISOString();
    const timestamps = { ...state.stepTimestamps, observableSignsCompletedAt: now };
    if (state.selectedObservableSigns.length > 0) {
      setState({
        ...state,
        currentStep: "outcome",
        outcome: "possible_concussion",
        completedAt: new Date(),
        stepTimestamps: timestamps,
      });
    } else {
      setState((prev) => ({
        ...prev,
        currentStep: "symptoms",
        stepTimestamps: timestamps,
      }));
    }
  };

  const handleSymptomsNext = () => {
    const now = new Date().toISOString();
    const timestamps = { ...state.stepTimestamps, selfSymptomsCompletedAt: now };
    setState((prev) => ({ ...prev, currentStep: "awareness", stepTimestamps: timestamps }));
  };

  const handleAwarenessAnswer = (id: string, value: 'normal' | 'impaired') => {
    setState((prev) => ({
      ...prev,
      awarenessAnswers: { ...prev.awarenessAnswers, [id]: value },
    }));
  };

  const handleAwarenessNext = () => {
    const now = new Date().toISOString();
    const timestamps = { ...state.stepTimestamps, awarenessCompletedAt: now };
    const hasImpaired = Object.values(state.awarenessAnswers).includes('impaired');
    const outcome: OutcomeType = hasImpaired
      ? 'possible_concussion'
      : state.selectedSelfSymptoms.length > 0
      ? 'monitoring'
      : 'no_signs';
    setState({ ...state, currentStep: 'outcome', outcome, completedAt: new Date(), stepTimestamps: timestamps });
  };

  const handleCompleteSave = () => {
    setAssessorNameReady(true);
    if (state.outcome) {
      saveIncident(state.outcome, state);
    }
  };

  const handleRestart = () => {
    sessionStorage.removeItem(ASSESSMENT_SESSION_KEY);
    savedProgressRef.current = null;
    setStarted(false);
    setSubjectName("");
    setSelectedSport("");
    setAssessorName("");
    setAssessorNameReady(false);
    setIncidentRecord(null);
    setSaveError(false);
    setIsSaving(false);
    hasSavedRef.current = false;
    setState(INITIAL_STATE);
    setManualAddress("");
    setLocationSkipped(false);
    setEmailSent(false);
    setSentToEmail(null);
    setEmailError(null);
    setEmailExistsInDb(null);
    setPostAction('none');
  };

  // Post-assessment action handlers
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailCopy = async (email: string) => {
    setEmailSending(true);
    setEmailError(null);

    console.log('[AssessmentPage] Sending assessment email to:', email);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      // Build request body — use incident_id when available, inline data as fallback
      const body: Record<string, unknown> = {
        recipient_email: email,
        org_id: orgData?.org_id ?? null,
        session_id: anonymousSession?.sessionId ?? '',
      };

      if (incidentRecord) {
        body.incident_id = incidentRecord.id;
      } else {
        // Send assessment data inline when incident wasn't saved to DB
        body.assessment_data = {
          selected_red_flags: state.selectedRedFlags,
          selected_observable_signs: state.selectedObservableSigns,
          selected_self_symptoms: state.selectedSelfSymptoms,
          awareness_answers: state.awarenessAnswers,
          started_at: state.startedAt?.toISOString() ?? '',
          completed_at: state.completedAt?.toISOString() ?? new Date().toISOString(),
        };
        body.subject_name = subjectName.trim() || null;
        body.outcome = state.outcome;
        body.red_flags_detected = state.selectedRedFlags.length > 0;
      }

      const response = await fetch(`${apiUrl}/api/charlie/assessment/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('[AssessmentPage] Email send failed:', result.error);
        setEmailError(result.error || 'Failed to send email. You can still sign up below.');
      } else {
        console.log('[AssessmentPage] Email sent successfully:', result.email_id);
        setEmailExistsInDb(result.user_exists === true);
      }
    } catch (error) {
      console.error('[AssessmentPage] Email send request failed:', error);
      setEmailError('Could not reach the server. You can still sign up below.');
    }

    setEmailSending(false);
    setEmailSent(true);
    setSentToEmail(email);
    setPostAction('none');
  };

  const handleClubShareComplete = () => {
    setPostAction('none');
  };

  const handleSendToClub = async () => {
    if (!orgEmail || !incidentRecord) return;
    setClubEmailSending(true);
    setClubEmailError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/charlie/assessment/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_email: orgEmail,
          incident_id: incidentRecord.id,
          org_id: orgData?.org_id ?? null,
          session_id: anonymousSession?.sessionId ?? '',
        }),
      });
      if (!response.ok) {
        const result = await response.json();
        setClubEmailError(result.error || 'Could not send — try again');
      } else {
        setClubEmailSent(true);
      }
    } catch {
      setClubEmailError('Could not reach the server — try again');
    } finally {
      setClubEmailSending(false);
    }
  };

  const handleRegister = () => {
    const url = sentToEmail
      ? `/signup?email=${encodeURIComponent(sentToEmail)}`
      : '/signup';
    window.location.href = url;
  };

  const handleDownload = async () => {
    const ad = incidentRecord?.assessment_data;
    await downloadAssessmentAsPdf({
      subjectName: (incidentRecord?.subject_name || subjectName.trim()) || 'Unknown',
      outcome: (incidentRecord?.outcome || state.outcome || 'Completed').toString(),
      redFlagsDetected: incidentRecord
        ? (incidentRecord.red_flags_detected as boolean)
        : state.selectedRedFlags.length > 0,
      redFlags: (ad?.selected_red_flags ?? state.selectedRedFlags) as string[],
      observableSigns: (ad?.selected_observable_signs ?? state.selectedObservableSigns) as string[],
      selfSymptoms: (ad?.selected_self_symptoms ?? state.selectedSelfSymptoms) as string[],
      incidentRef: incidentRecord?.incident_ref ?? 'PENDING',
      createdAt: incidentRecord?.created_at ?? state.completedAt?.toISOString() ?? new Date().toISOString(),
    });
  };

  const handleRetry = () => {
    if (state.outcome) {
      hasSavedRef.current = false;
      saveIncident(state.outcome, state);
    }
  };

  const handleBackFromOutcome = () => {
    const awarenessWasReached = Object.keys(state.awarenessAnswers).length > 0;
    const previousStep: AssessmentStep =
      state.outcome === "emergency"
        ? "red-flags"
        : state.outcome === "possible_concussion" && !awarenessWasReached
        ? "observable-signs"
        : state.outcome === "possible_concussion" && awarenessWasReached
        ? "awareness"
        : "awareness";
    hasSavedRef.current = false;
    setIncidentRecord(null);
    setSaveError(false);
    setIsSaving(false);
    setEmailSent(false);
    setSentToEmail(null);
    setEmailError(null);
    setEmailExistsInDb(null);
    setPostAction('none');
    // Emergency outcome lands on red-flags. If org auto-selected the sport (no sport-select
    // screen was shown), there's nowhere to go back to between start and red-flags —
    // drop to start screen cleanly so the user can begin again with org context intact.
    if (previousStep === "red-flags" && hasOrgSports) {
      setStarted(false);
      setState(INITIAL_STATE);
    } else {
      setState((prev) => ({
        ...prev,
        currentStep: previousStep,
        outcome: null,
        completedAt: null,
      }));
    }
  };

  const location = useLocation();
  // Both /assessment/public and /assessment/:orgSlug are anonymous flows
  const isPublic = location.pathname.startsWith("/assessment/");
  const homeLink = isPublic ? "/welcome" : "/home";

  return (
    <div className={theme.layout.containerSm}>
      <Link to={homeLink} className={`inline-flex items-center gap-1.5 ${theme.button.back} mb-6`}>
        <Home className="w-4 h-4" />
        Home
      </Link>

      {/* Org banner — shown when org has sports mapping */}
      {orgData && orgSportLabel && (
        <OrgBanner orgName={orgData.org_name} sportLabel={orgSportLabel} />
      )}

      {started && <ProgressIndicator currentStep={state.currentStep} />}

      {!started && (
        <StartScreen
          onStart={handleStart}
          hasProgress={hasProgress}
          onResume={handleResume}
          onStartFresh={handleStartFresh}
          isLoggedIn={isLoggedIn}
          locationCaptureCard={
            !hasSessionGeo ? (
              <LocationCaptureCard
                permissionState={geoPermissionState}
                isLoading={geoIsLoading}
                address={geoAddress}
                coordinates={geoCoordinates}
                manualAddress={manualAddress}
                skipped={locationSkipped}
                onRequestLocation={requestLocation}
                onManualAddressChange={setManualAddress}
                onSkip={() => setLocationSkipped(true)}
              />
            ) : null
          }
        />
      )}

      {started && state.currentStep === "sport-select" && (
        <SportSelectScreen
          selectedSport={selectedSport}
          onSelect={setSelectedSport}
          onNext={handleSportNext}
          onBack={() => {
            savedProgressRef.current = { state, sport: selectedSport };
            setStarted(false);
          }}
        />
      )}

      {started && state.currentStep === "red-flags" && (
        <RedFlagScreen
          selectedFlags={state.selectedRedFlags}
          onToggleFlag={handleToggleRedFlag}
          onNext={handleRedFlagNext}
          onBack={handleBack}
        />
      )}

      {started && state.currentStep === "observable-signs" && (
        <ObservableSignsScreen
          selectedSigns={state.selectedObservableSigns}
          onToggleSign={handleToggleObservableSign}
          onNext={handleObservableSignsNext}
          onBack={handleBack}
        />
      )}

      {started && state.currentStep === "symptoms" && (
        <SelfSymptomsScreen
          selectedSymptoms={state.selectedSelfSymptoms}
          onToggleSymptom={handleToggleSelfSymptom}
          onNext={handleSymptomsNext}
          onBack={handleBack}
        />
      )}

      {started && state.currentStep === "awareness" && (
        <AwarenessScreen
          answers={state.awarenessAnswers}
          onAnswer={handleAwarenessAnswer}
          onNext={handleAwarenessNext}
          onBack={handleBack}
        />
      )}

      {started && state.currentStep === "outcome" && state.outcome && (
          <OutcomeScreen
              outcome={state.outcome}
              subjectName={subjectName}
              sport={selectedSport}
              assessmentState={state}
              onRestart={handleRestart}
              onBack={handleBackFromOutcome}
              sentToEmail={sentToEmail}
              isPublic={isPublic}
              isLoggedIn={isLoggedIn}
              onRegister={handleRegister}
              emailSent={emailSent}
              emailExistsInDb={emailExistsInDb}
              beforeRestart={
                <>
                  {/* Logged-in users: Share + Download as separate top-level buttons */}
                  {isLoggedIn && (
                    <div className={`mt-4 space-y-3 ${theme.anim.fadeInUp}`}>
                      {/* Share assessment — expands to email form only */}
                      {!showSharePanel && !emailSent && (
                        <button
                          onClick={() => setShowSharePanel(true)}
                          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.03] bg-white dark:hover:bg-white/[0.06] hover:bg-gray-50 ${theme.anim.transition} text-left`}
                        >
                          <div className="w-10 h-10 rounded-xl dark:bg-sy-teal/[0.12] bg-teal-50 flex items-center justify-center flex-shrink-0">
                            <Share2 className="w-5 h-5 text-sy-teal" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`${theme.text.heading} font-medium text-sm`}>Share assessment</p>
                            <p className={`${theme.text.muted} text-xs`}>Email a copy of this report</p>
                          </div>
                          <ChevronRight className={`w-4 h-4 ${theme.text.muted} flex-shrink-0`} />
                        </button>
                      )}
                      {(showSharePanel || emailSent) && (
                        <div className={`space-y-3 ${theme.anim.fadeInUp}`}>
                          <button
                            onClick={() => { setShowSharePanel(false); setEmailSent(false); setSentToEmail(null); setEmailError(null); }}
                            className={`${theme.button.back} mb-1`}
                          >
                            <ArrowLeft className="w-4 h-4" /> Back
                          </button>
                          {!emailSent && (
                            <EmailCopyForm
                              onSubmit={handleEmailCopy}
                              onCancel={() => {}}
                              isSending={emailSending}
                              heading="Share Your Assessment Report"
                              description="Enter the email address of your chosen recipient and we'll send the assessment summary to that person."
                              buttonLabel="Share Assessment Report"
                            />
                          )}
                          {emailSent && (
                            <div className={`${theme.card.paddedMuted}`}>
                              {emailError ? (
                                <div className="flex items-center gap-3">
                                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                  <p className={`${theme.text.heading} text-sm font-medium`}>{emailError}</p>
                                </div>
                              ) : (
                                <div className="flex items-center gap-3">
                                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                  <p className={`${theme.text.heading} text-sm font-medium`}>Assessment report sent to your email.</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Download assessment — always separate, not inside share */}
                      <button
                        onClick={handleDownload}
                        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.03] bg-white dark:hover:bg-white/[0.06] hover:bg-gray-50 ${theme.anim.transition} text-left`}
                      >
                        <div className="w-10 h-10 rounded-xl dark:bg-sy-teal/[0.12] bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Download className="w-5 h-5 text-sy-teal" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`${theme.text.heading} font-medium text-sm`}>Download assessment</p>
                          <p className={`${theme.text.muted} text-xs`}>Save a copy to your device</p>
                        </div>
                        <ChevronRight className={`w-4 h-4 ${theme.text.muted} flex-shrink-0`} />
                      </button>
                    </div>
                  )}

                  {/* Step 1: Email form shown directly after assessment (anonymous users only) */}
                  {!isLoggedIn && !emailSent && postAction !== 'club_share' && (
                    <EmailCopyForm
                      onSubmit={handleEmailCopy}
                      onCancel={() => {}}
                      isSending={emailSending}
                    />
                  )}

                  {/* Step 2: After email sent — confirmation + club sharing */}
                  {!isLoggedIn && emailSent && postAction === 'none' && (
                    <div className={`mt-6 space-y-4 ${theme.anim.fadeInUp}`}>
                      {/* Email confirmation */}
                      <div className={`${theme.card.paddedMuted}`}>
                        <button onClick={() => { setEmailSent(false); setSentToEmail(null); setEmailError(null); }} className={`${theme.button.back} mb-3`}>
                          <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        {emailError ? (
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <p className={`${theme.text.heading} text-sm font-medium`}>{emailError}</p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                            <p className={`${theme.text.heading} text-sm font-medium`}>Assessment report sent to your email.</p>
                          </div>
                        )}
                      </div>

                      {/* Email my club a copy — shown when org has a contact email */}
                      {orgEmail && incidentRecord && (
                        <div>
                          {clubEmailSent ? (
                            <div className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border dark:border-emerald-500/20 border-emerald-200 dark:bg-emerald-500/[0.06] bg-emerald-50 ${theme.anim.fadeInUp}`}>
                              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                              <p className={`${theme.text.heading} text-sm font-medium`}>Sent to your club ✓</p>
                            </div>
                          ) : (
                            <button
                              onClick={handleSendToClub}
                              disabled={clubEmailSending}
                              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border dark:border-white/[0.08] border-gray-200 dark:bg-white/[0.03] bg-white dark:hover:bg-white/[0.06] hover:bg-gray-50 ${theme.anim.transition} text-left disabled:opacity-60`}
                            >
                              <div className="w-10 h-10 rounded-xl dark:bg-sy-teal/[0.12] bg-teal-50 flex items-center justify-center flex-shrink-0">
                                {clubEmailSending ? (
                                  <Loader2 className={`w-5 h-5 text-sy-teal ${theme.anim.spin}`} />
                                ) : (
                                  <Mail className="w-5 h-5 text-sy-teal" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`${theme.text.heading} font-medium text-sm`}>Email my club a copy</p>
                                <p className={`${theme.text.muted} text-xs`}>Send this report to {orgData?.org_name ?? 'your club'}</p>
                              </div>
                              <ChevronRight className={`w-4 h-4 ${theme.text.muted} flex-shrink-0`} />
                            </button>
                          )}
                          {clubEmailError && (
                            <p className={`text-xs text-red-500 mt-1.5 ml-1`}>{clubEmailError}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Club sharing workflow */}
                  {postAction === 'club_share' && orgData && (
                    <ClubSharingScreen
                      orgId={orgData.org_id}
                      orgName={orgData.org_name}
                      sessionId={anonymousSession?.sessionId ?? ''}
                      incidentId={incidentRecord?.id}
                      userId={anonymousSession?.anonUserId}
                      userGeo={anonymousSession?.userGeolocation ?? geoCoordinates}
                      onComplete={handleClubShareComplete}
                      onCancel={() => setPostAction('none')}
                    />
                  )}
                </>
              }
            />
      )}
    </div>
  );
};
